import { Liferay } from './liferay.js';
 
/**
 * Liferay API service for callback requests
 */
class CallbackAPI {
  constructor() {
    this.baseURL = this.getPortalURL();
    this.endpoint = '/o/c/requestacallbacks/scopes/';
  }
 
  /**
   * Get portal URL
   */
  getPortalURL() {
    if (typeof window !== 'undefined' && window.Liferay && window.Liferay.ThemeDisplay) {
      return window.Liferay.ThemeDisplay.getPortalURL();
    }
    return window.location.origin;
  }
 
  /**
   * Get CSRF token
   */
  getCsrfToken() {
    if (typeof window !== 'undefined') {
      // Try to get from meta tag first
      const csrfToken = document.querySelector('meta[name="_csrf"]');
      if (csrfToken) {
        return csrfToken.getAttribute('content');
      }
     
      // Fallback to Liferay.authToken
      if (Liferay.authToken) {
        return Liferay.authToken;
      }
    }
    return null;
  }
 
  /**
   * Get user information
   */
  getUser() {
    if (typeof window !== 'undefined' && window.Liferay && window.Liferay.ThemeDisplay) {
      return {
        userId: window.Liferay.ThemeDisplay.getUserId(),
        userName: window.Liferay.ThemeDisplay.getUserName(),
        emailAddress: window.Liferay.ThemeDisplay.getUserEmailAddress(),
        languageId: window.Liferay.ThemeDisplay.getLanguageId(),
        scopeGroupId: Liferay.ThemeDisplay.getScopeGroupId(),
        companyId: window.Liferay.ThemeDisplay.getCompanyId(),
        plid: window.Liferay.ThemeDisplay.getPlid()
      };
    }
    return {
      userId: '',
      userName: '',
      emailAddress: '',
      languageId: 'en_US',
      scopeGroupId: Liferay.ThemeDisplay.getScopeGroupId() || '',
      companyId: '',
      plid: ''
    };
  }
 
  /**
   * Generate external reference code
   */
  generateExternalReferenceCode() {
    return `callback_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
 
  /**
   * Format date for Liferay API (ISO format)
   */
  formatDateForAPI(dateString) {
    if (!dateString) return null;
   
    const date = new Date(dateString);
    // Set time to noon to avoid timezone issues
    //date.setHours(12, 0, 0, 0);
    date.setHours(date.getHours() + 6);
    return date.toISOString();
  }
 
  /**
   * Get default headers for API requests
   */
  async getHeaders() {
    const headers = {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    };
 
    // Add CSRF token if available
    //const csrfToken = this.getCsrfToken();
    //if (csrfToken) {
      //headers['x-csrf-token'] = csrfToken;
    //}
 
    // Add auth token if available
    const token = await this.getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
 
    return headers;
  }
 
 
 
 async getAccessToken() {
  try {
    const response = await fetch("/o/tokenService/get-token", {
      method: "GET",
      headers: {
        "Accept": "application/json",
        "Content-Type": "application/json"
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Token request failed: ${response.status} - ${errorText}`);
    }

    const result = await response.json();

    // Extract token from your response structure
    return result?.data?.accessToken || null;

  } catch (error) {
    if (globalThis.process?.env?.NODE_ENV !== 'test') {
      console.error("Error fetching access token:", error);
    }
    return null;
  }
}
 
 
  /**
   * Create a new callback request
   * @param {Object} callbackData - The callback form data
   * @returns {Promise} API response
   */
  async createCallbackRequest(callbackData,requestType) {
    try {
      const user = this.getUser();
      const headers = await this.getHeaders();
     
      // Prepare the payload according to the API specification
      const payload = {
        externalReferenceCode: this.generateExternalReferenceCode(),
        name: callbackData.name,
        emailAddress: callbackData.email,
        phoneNumber: callbackData.phone,
        pickADate: this.formatDateForAPI(callbackData.date),
        comment: callbackData.comment || '',
        scopeGroupId: user.scopeGroupId || '',
        requestType: requestType,
        keywords: ['callback', 'request'],
        taxonomyCategoryIds: [],
        permissions: []
      };
 
      console.log('API Request Details:', {
        url: `${this.baseURL}${this.endpoint}${user.scopeGroupId}`,
        headers: headers,
        payload: payload,
        user: user
      });
 
      const response = await fetch(`${this.baseURL}${this.endpoint}${user.scopeGroupId}`, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(payload),
        credentials: 'include' // Include cookies for authentication
      });
 
      console.log('API Response Status:', response.status, response.statusText);
 
      if (!response.ok) {
        const errorText = await response.text();
        if (globalThis.process?.env?.NODE_ENV !== 'test') {
          console.error('API Error Response:', {
            status: response.status,
            statusText: response.statusText,
            body: errorText,
            headers: Object.fromEntries(response.headers.entries())
          });
        }
       
        let errorMessage = 'Failed to submit callback request';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorData.title || errorData.error || errorMessage;
        } catch (e) {
          // Use the raw error text if JSON parsing fails
          errorMessage = errorText || errorMessage;
        }
       
        throw new Error(`${errorMessage} (Status: ${response.status})`);
      }
 
      const result = await response.json();
      console.log('Callback request created successfully:', result);
      return result;
 
    } catch (error) {
      if (globalThis.process?.env?.NODE_ENV !== 'test') {
        console.error('Error creating callback request:', error);
      }
     
      // Re-throw with more context if it's a network error
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to the server. Please check your internet connection.');
      }
     
      throw error;
    }
  }
 
  /**
   * Get callback requests (for admin purposes)
   * @returns {Promise} API response
   */
  async getCallbackRequests() {
    try {
      const response = await fetch(`${this.baseURL}${this.endpoint}`, {
        method: 'GET',
        headers: await this.getHeaders(),
        credentials: 'include'
      });
 
      if (!response.ok) {
        throw new Error(`Failed to fetch callback requests (Status: ${response.status})`);
      }
 
      return await response.json();
    } catch (error) {
      if (globalThis.process?.env?.NODE_ENV !== 'test') {
        console.error('Error fetching callback requests:', error);
      }
      throw error;
    }
  }
 
 
  /**
   * Create a new job application using custom REST service
   * @param {Object} jobData - The job application form data
   * @returns {Promise} API response
   */
  async createJobApplication(jobData, ourAgencyForceForm) {
    try {
      const user = this.getUser();
     
      // Create FormData for file uploads matching the curl request format
      const formData = new FormData();
     
      // Add form fields exactly as in the curl request
      formData.append('firstName', jobData.firstName);
      formData.append('middleName', jobData.middleName || '');
      formData.append('surname', jobData.surname);
      formData.append('email', jobData.email);
      formData.append('phoneNumber', jobData.phoneNumber);
      formData.append('jobAppliedFor', jobData.jobAppliedFor);
      formData.append('education', jobData.education);
      formData.append('qualifications', jobData.qualifications);
      formData.append('scopeGroupId', user.scopeGroupId || '250023'); // Default fallback from curl
     
      // Add CV file if it exists (matching curl format)
      if (jobData.cvFile) {
        formData.append('cv', jobData.cvFile);
      }
     
      // Add cover letter file if it exists
      if (jobData.coverLetterFile) {
        formData.append('coverLetter', jobData.coverLetterFile);
      }
 
      // Use the custom REST service endpoint
      const jobEndpoint = ourAgencyForceForm ? '/o/restService/form-sumbission/agency-force-recruitment' : '/o/restService/form-sumbission/open-roles';
     
      console.log('Sending job application to custom REST service:', {
        endpoint: `${this.baseURL}${jobEndpoint}`,
        formData: Object.fromEntries(formData.entries()),
        user: user
      });
 
      // Prepare headers for authentication
      const headers = {
        'Accept': 'application/json',
      };
 
      // Use OAuth token for authentication
      const token = await this.getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
      // Use CSRF token for authentication
      //const csrfToken = this.getCsrfToken();
      //if (csrfToken) {
        //headers['x-csrf-token'] = csrfToken;
      //}
 
      // Don't set Content-Type for FormData - let browser handle it
 
      const response = await fetch(`${this.baseURL}${jobEndpoint}`, {
        method: 'POST',
        headers: headers,
        body: formData,
        credentials: 'include'
      });
 
      console.log('Job Application API Response Status:', response.status, response.statusText);
 
      if (!response.ok) {
        const errorText = await response.text();
        if (globalThis.process?.env?.NODE_ENV !== 'test') {
          console.error('Job Application API Error Response:', {
            status: response.status,
            statusText: response.statusText,
            body: errorText,
            headers: Object.fromEntries(response.headers.entries())
          });
        }
       
        let errorMessage = 'Failed to submit job application';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorData.title || errorData.error || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }
       
        throw new Error(`${errorMessage} (Status: ${response.status})`);
      }
 
      const result = await response.json();
      console.log('Job application submitted successfully:', result);
      return result;
 
    } catch (error) {
      if (globalThis.process?.env?.NODE_ENV !== 'test') {
        console.error('Error submitting job application:', error);
      }
     
      // Re-throw with more context if it's a network error
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to the server. Please check your internet connection.');
      }
     
      throw error;
    }
  }
 
  /**
   * Create a new academy application using custom REST service
   * @param {Object} academyData - The academy application form data
   * @returns {Promise} API response
   */
  async createAcademyApplication(academyData) {
    try {
      const user = this.getUser();
     
      // Create FormData for file uploads matching the curl request format
      const formData = new FormData();
     
      // Add form fields exactly as in the curl request
 
      formData.append('title', academyData.title);
      formData.append('firstName', academyData.firstName);
      formData.append('middleName', academyData.middleName || '');
      formData.append('lastName', academyData.lastName); // Note: lastName maps to surname
      formData.append('emailAddress', academyData.emailAddress); // Note: emailAddress maps to email
      formData.append('primaryPhoneNumber', academyData.primaryPhoneNumber); // Note: primaryPhoneNumber maps to phoneNumber
      formData.append('location', academyData.location); // Using location as education field
      formData.append('numberOfPoliciesSold', academyData.numberOfPoliciesSold); // Include policies sold info
      formData.append('scopeGroupId', user.scopeGroupId || '250023'); // Default fallback from curl
     
      // Add CV file if it exists (matching curl format)
      if (academyData.cvFile) {
        formData.append('cv', academyData.cvFile);
      }
 
      // Use the custom REST service endpoint for agency force recruitment
      const academyEndpoint = '/o/restService/form-sumbission/icealion-academy';
     
      console.log('Sending academy application to custom REST service:', {
        endpoint: `${this.baseURL}${academyEndpoint}`,
        formData: Object.fromEntries(formData.entries()),
        user: user
      });
 
      // Prepare headers for authentication
      const headers = {
        'Accept': 'application/json',
      };
 
      // Use CSRF token for authentication
      //const csrfToken = this.getCsrfToken();
      //if (csrfToken) {
        //headers['x-csrf-token'] = csrfToken;
      //}

       // Use OAuth token for authentication
      const token = await this.getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
 
      // Don't set Content-Type for FormData - let browser handle it
 
      const response = await fetch(`${this.baseURL}${academyEndpoint}`, {
        method: 'POST',
        headers: headers,
        body: formData,
        credentials: 'include'
      });
 
      console.log('Academy Application API Response Status:', response.status, response.statusText);
 
      if (!response.ok) {
        const errorText = await response.text();
        if (globalThis.process?.env?.NODE_ENV !== 'test') {
          console.error('Academy Application API Error Response:', {
            status: response.status,
            statusText: response.statusText,
            body: errorText,
            headers: Object.fromEntries(response.headers.entries())
          });
        }
       
        let errorMessage = 'Failed to submit academy application';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.message || errorData.title || errorData.error || errorMessage;
        } catch (e) {
          errorMessage = errorText || errorMessage;
        }
       
        throw new Error(`${errorMessage} (Status: ${response.status})`);
      }
 
      const result = await response.json();
      console.log('Academy application submitted successfully:', result);
      return result;
 
    } catch (error) {
      if (globalThis.process?.env?.NODE_ENV !== 'test') {
        console.error('Error submitting academy application:', error);
      }
     
      // Re-throw with more context if it's a network error
      if (error instanceof TypeError && error.message.includes('fetch')) {
        throw new Error('Network error: Unable to connect to the server. Please check your internet connection.');
      }
     
      throw error;
    }
  }
}
 
// Create and export a singleton instance
const api = new CallbackAPI();
 
export default api;